import {
  Accordion,
  Badge,
  Button,
  cn,
  ConfirmableDeleteButton,
  Icon,
  Input,
  SearchableCheckboxList,
  Select,
  Text,
} from '@risksmart-app/atomic-ui';
import { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CONDITIONAL_OPERATOR_LABELS } from '../config';
import type { ConditionalLogicOption } from '../types';

const SINGLE_VALUE_OPERATORS = new Set(['!=', '!:']);

export const ConditionalLogicRuleBuilder = ({
  fieldOptions,
  getValueOptions,
  currentEditingId,
  disallowedCycleCandidates,
  description,
  errorMessage,
  locked = false,
}: {
  fieldOptions: ConditionalLogicOption[];
  getValueOptions?: (fieldValue: string) => ConditionalLogicOption[];
  currentEditingId?: string;
  disallowedCycleCandidates?: Set<string>;
  description?: string;
  errorMessage?: string | null;
  /**
   * Locks the whole builder read-only: no field/value editing, no rule removal,
   * no "Add rule" button. Used for strict-replace read-only default conditions.
   * Merge mode does NOT set this — it locks individual rows via each rule's
   * `locked` flag instead, leaving "Add rule" and customer rows editable.
   */
  locked?: boolean;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'formEditorConditionalLogic',
  });
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditionalLogicRules',
  });

  // Each rule box can be collapsed independently to tuck its detail away once
  // several rules are configured, so a long conditional-logic section stays
  // navigable (CUS-200). UI-only, in-session state keyed by the field-array
  // `id` (stable across re-renders); rules default to expanded and collapsing
  // never touches form state.
  const [collapsedRuleIds, setCollapsedRuleIds] = useState<Set<string>>(
    () => new Set()
  );

  const filteredFieldOptions = fieldOptions.filter(
    (opt) =>
      opt.value !== currentEditingId &&
      // Undefined → allow; only an explicit false rules a field out.
      opt.allowAsSource !== false
  );

  const showOperationSelect = fields.length >= 2;

  // Drive collapse through the shared Accordion primitive so rule boxes open
  // and close with the same height animation as every other collapsible
  // section. We track the *collapsed* exception set (UI-only, keyed by the
  // field-array `id`) so rules — including newly added ones — default to
  // expanded, then derive the expanded-id list the Accordion consumes.
  const allRuleIds = fields.map((rule) => rule.id);
  const expandedRuleIds = allRuleIds.filter((id) => !collapsedRuleIds.has(id));
  const handleExpandedRuleIdsChange = (nextExpandedIds: string[]) => {
    const nextExpandedSet = new Set(nextExpandedIds);
    setCollapsedRuleIds(
      new Set(allRuleIds.filter((id) => !nextExpandedSet.has(id)))
    );
  };

  return (
    <div className={cn('flex flex-col gap-4 pt-4')}>
      <Text preset={'body-md'} className={cn('font-semibold m-0')}>
        {description ?? t('defaultDescription')}
      </Text>
      {locked ? (
        <Text
          preset={'body-sm'}
          className={cn('text-neutral-muted-foreground')}
        >
          {t('lockedNotice')}
        </Text>
      ) : null}
      {!locked && showOperationSelect ? (
        <Controller
          control={control}
          name={'conditionalLogicOperation'}
          render={({ field: operationField }) => (
            <div className={cn('flex items-center gap-3')}>
              <Text preset={'body-sm'} className={'font-semibold shrink-0'}>
                {t('showWhen')}
              </Text>
              <Select
                items={[
                  { value: 'and', label: t('operation.and') },
                  { value: 'or', label: t('operation.or') },
                ]}
                value={operationField.value ?? 'and'}
                onValueChange={operationField.onChange}
              />
            </div>
          )}
        />
      ) : null}
      <Accordion
        multiple
        value={expandedRuleIds}
        onValueChange={handleExpandedRuleIdsChange}
      >
        {fields.map((rule, index) => {
          const selectedIfField = watch(
            `conditionalLogicRules.${index}.ifField`
          );
          const selectedOperator =
            watch(`conditionalLogicRules.${index}.operator`) ?? '=';
          // A row is read-only when the whole builder is locked (strict-replace)
          // or when this individual rule is a locked default (merge mode).
          const rowLocked =
            locked || watch(`conditionalLogicRules.${index}.locked`) === true;
          const valueOptions = getValueOptions?.(selectedIfField) ?? [];
          const selectedFieldMeta = filteredFieldOptions.find(
            (opt) => opt.value === selectedIfField
          );
          const isFreeText = selectedFieldMeta?.freeText === true;
          const operatorOptions = selectedFieldMeta?.operatorOptions;
          const showOperatorSelect =
            operatorOptions !== undefined && operatorOptions.length > 1;
          const isSingleValueOperator =
            SINGLE_VALUE_OPERATORS.has(selectedOperator);

          const allSelectedIfFields = fields
            .map((_, i) => watch(`conditionalLogicRules.${i}.ifField`))
            .filter(Boolean);

          // Keep the currently-selected value even if it'd otherwise be hidden,
          // so a stale form value never renders as an empty Select. The schema
          // backstop still catches any cycle the user submits.
          const availableFieldOptions = filteredFieldOptions.filter(
            (opt) =>
              opt.value === selectedIfField ||
              (!allSelectedIfFields.includes(opt.value) &&
                !disallowedCycleCandidates?.has(opt.value))
          );

          const operatorLabel =
            CONDITIONAL_OPERATOR_LABELS[selectedOperator] ?? 'Is equal to';

          const ruleError = (
            errors.conditionalLogicRules as
              | Record<string, unknown>[]
              | undefined
          )?.[index];

          return (
            <Accordion.Item
              key={rule.id}
              value={rule.id}
              variant={'card'}
              data-slot={'conditional-logic-rule'}
              className={cn(
                'rounded-lg bg-neutral-minimal',
                ruleError ? 'border-destructive' : 'border-neutral-border'
              )}
            >
              <Accordion.Header
                variant={'card'}
                className={cn('items-center justify-between pr-3')}
              >
                <Accordion.Trigger
                  variant={'card'}
                  className={cn(
                    'flex-1 gap-2 p-4 text-base font-normal bg-transparent'
                  )}
                >
                  <span className={cn('flex items-center gap-2 min-w-0')}>
                    <Text preset={'body-md'} className={'font-semibold'}>
                      {t('ruleLabel', { number: index + 1 })}
                    </Text>
                    {rowLocked ? (
                      <Badge variant={'neutral'} size={'sm'} border={true}>
                        {t('managedBadge')}
                      </Badge>
                    ) : null}
                  </span>
                </Accordion.Trigger>
                {rowLocked ? null : (
                  <ConfirmableDeleteButton
                    onConfirm={() => remove(index)}
                    size={'icon'}
                  />
                )}
              </Accordion.Header>
              <Accordion.Content
                variant={'card'}
                className={cn('flex flex-col gap-3 pt-0')}
              >
                <Controller
                  control={control}
                  name={`conditionalLogicRules.${index}.ifField`}
                  render={({ field }) => (
                    <div className={cn('flex items-center gap-3')}>
                      <Text
                        preset={'body-sm'}
                        className={'font-semibold shrink-0 w-[60px]'}
                      >
                        {t('ifLabel')}
                      </Text>
                      <Select
                        items={[
                          {
                            label:
                              availableFieldOptions.length > 0
                                ? t('pickField')
                                : t('noValidFields'),
                            value: null,
                          },
                          ...availableFieldOptions,
                        ]}
                        value={field.value || undefined}
                        onValueChange={(v) => {
                          field.onChange(v);
                          setValue(`conditionalLogicRules.${index}.values`, []);
                          // Reset operator to the default for the newly-selected field
                          const newFieldMeta = filteredFieldOptions.find(
                            (opt) => opt.value === v
                          );
                          const defaultOperator =
                            newFieldMeta?.operatorOptions?.[0]?.value ?? '=';
                          setValue(
                            `conditionalLogicRules.${index}.operator`,
                            defaultOperator
                          );
                        }}
                        disabled={
                          rowLocked || availableFieldOptions.length === 0
                        }
                      />
                    </div>
                  )}
                />
                {selectedIfField && showOperatorSelect ? (
                  <Controller
                    control={control}
                    name={`conditionalLogicRules.${index}.operator`}
                    render={({ field: operatorField }) => (
                      <div className={cn('flex items-center gap-3')}>
                        <Text
                          preset={'body-sm'}
                          className={'font-semibold shrink-0 w-[60px]'}
                        >
                          {''}
                        </Text>
                        <Select
                          items={operatorOptions}
                          value={operatorField.value || '='}
                          onValueChange={(v) => {
                            operatorField.onChange(v);
                            setValue(
                              `conditionalLogicRules.${index}.values`,
                              []
                            );
                          }}
                          disabled={rowLocked}
                        />
                      </div>
                    )}
                  />
                ) : null}
                {selectedIfField && isFreeText ? (
                  <Controller
                    control={control}
                    name={`conditionalLogicRules.${index}.values`}
                    render={({ field: valuesField }) => (
                      <div className={cn('flex items-center gap-3')}>
                        {!showOperatorSelect ? (
                          <Text
                            preset={'body-sm'}
                            className={'font-semibold shrink-0 w-[60px]'}
                          >
                            {operatorLabel}
                          </Text>
                        ) : (
                          <div className={cn('shrink-0 w-[60px]')} />
                        )}
                        <Input
                          value={(valuesField.value ?? [])[0] ?? ''}
                          onChange={(e) =>
                            valuesField.onChange(
                              e.target.value === '' ? [] : [e.target.value]
                            )
                          }
                          placeholder={t('enterValue')}
                          disabled={rowLocked}
                        />
                      </div>
                    )}
                  />
                ) : null}
                {selectedIfField && !isFreeText && valueOptions.length > 0 ? (
                  <Controller
                    control={control}
                    name={`conditionalLogicRules.${index}.values`}
                    render={({ field: valuesField }) => {
                      if (isSingleValueOperator) {
                        // != and !: → single-value Select
                        return (
                          <div className={cn('flex items-center gap-3')}>
                            <div className={cn('shrink-0 w-[60px]')} />
                            <Select
                              items={[
                                { label: t('pickValue'), value: null },
                                ...valueOptions,
                              ]}
                              value={(valuesField.value ?? [])[0] || undefined}
                              onValueChange={(v) =>
                                valuesField.onChange(v ? [v] : [])
                              }
                              disabled={rowLocked}
                            />
                          </div>
                        );
                      }

                      // = and : → multi-value searchable, virtualised checkbox picker
                      return (
                        <div className={cn('flex items-start gap-3')}>
                          {!showOperatorSelect ? (
                            <Text
                              preset={'body-sm'}
                              className={'font-semibold shrink-0 w-[60px] mt-4'}
                            >
                              {operatorLabel}
                            </Text>
                          ) : (
                            <div className={cn('shrink-0 w-[60px]')} />
                          )}
                          <div
                            className={cn(
                              'flex-1 min-w-0 rounded-md border border-neutral-border p-3 flex flex-col gap-2 bg-neutral'
                            )}
                          >
                            <Text preset={'body-md'}>
                              {t('selectTriggerInputs')}
                            </Text>
                            <SearchableCheckboxList
                              options={valueOptions}
                              value={valuesField.value ?? []}
                              onChange={valuesField.onChange}
                              disabled={rowLocked}
                              searchPlaceholder={t('searchTriggerInputs')}
                              testId={`conditional-value-search-${index}`}
                            />
                          </div>
                        </div>
                      );
                    }}
                  />
                ) : null}
                {ruleError ? (
                  <Text
                    preset={'body-sm'}
                    className={cn('text-destructive pt-4')}
                  >
                    {t('ruleInvalid')}
                  </Text>
                ) : null}
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion>
      {locked ? null : (
        <Button
          style={'dashed-fill'}
          radius={'xl'}
          className={'w-full'}
          onClick={() =>
            append({ ifField: '', operator: '=', values: [], locked: false })
          }
        >
          <Icon name={'plus'} size={'sm'} />
          {t('addRule')}
        </Button>
      )}
      {errorMessage ? (
        <Text preset={'body-sm'} className={cn('text-destructive')}>
          {errorMessage}
        </Text>
      ) : null}
    </div>
  );
};
